// src/types/index.ts

// ==================== КАТЕГОРИИ ====================
export interface Category {
    id: number;
    name: string;
    description?: string;
    image_url?: string;
    parent_id?: number;
    level: number;
    is_active: boolean;
    created_at: string;
}

// ==================== ТОВАРЫ ====================
export interface Product {
    id: number;
    supplier_id: number;
    name: string;
    color?: string;
    images: string[];
    brand?: string;
    quantity_per_pack?: number;
    country?: string;
    composition?: string;
    production_date?: string;
    expiry_date?: string;
    size_or_volume?: string;
    stock_quantity: number;
    supplier_price?: number;
    status: 'pending' | 'approved' | 'rejected';
    is_available: boolean;
    contract_type?: 'FBS' | 'FBO';
    barcode?: string;
    season?: 'spring' | 'summer' | 'autumn' | 'winter' | 'demi' | 'all';
    gender?: 'male' | 'female' | 'unisex' | 'children';
    material?: string;
    product_code?: string;
    created_at: string;
    updated_at: string;
    variants?: ProductVariant[];
    main_image?: string;
    family_code?: string;
    description?: string;
}

// ==================== ВАРИАНТЫ ТОВАРОВ ====================
export interface ProductVariant {
    id: number;
    product_id: number;
    instance_id: string;
    size_or_volume?: string;
    color?: string;
    material?: string;
    attributes: Record<string, any>;
    quantity: number;
    price: number;
    is_active: boolean;
    received_date?: string;
    received_by?: string;
    created_at: string;
    updated_at: string;
    product?: Product;
    description?: string;
}


export interface PickupPoint {
    id: string;
    name: string;
    address: string;
    city: string;
    street: string;
    house: string;
    apartment?: string;
    phone?: string;
    working_hours?: string;
    coordinates?: {
        lat: number;
        lng: number;
    };
    is_active: boolean;
    created_at: string;
    updated_at: string;
}

// ==================== АДРЕСА ДОСТАВКИ ====================
export interface CustomerAddress {
    id: string;
    customer_id: string;
    country_code?: string;     // Код страны (TJ, RU, KZ и т.д.)
    region?: string;           // Регион/область
    city: string;              // Город/район
    street: string;
    house: string;
    apartment?: string;
    postal_code?: string;
    is_default: boolean;
    created_at: string;
    updated_at: string;
}

// ==================== ПОЛЬЗОВАТЕЛИ (КЛИЕНТЫ) ====================
export interface Customer {
    id: string;
    phone_number?: string;
    phone_country_code?: string;  // Код страны для телефона (+992, +7 и т.д.)
    first_name?: string;
    last_name?: string;
    country_code?: string;         // Префикс для заказа (TJ, RU, KZ и т.д.)
    is_active?: boolean;  // опционально, по умолчанию true

}

export type User = Customer;


// ==================== КОРЗИНА КЛИЕНТА ====================
export interface CartItem {
    id: string;
    customer_id: string;
    product_id: number;
    variant_id: number;
    quantity: number;
    created_at: string;
    updated_at: string;
    product?: Product;
    variant?: ProductVariant;
    is_selected?: boolean; // ✅ Поле для выбора товаров
    is_available?: boolean; // ✅ Поле для проверки наличия
}

// ✅ Добавляем вспомогательную функцию для проверки наличия товара
export const isCartItemAvailable = (item: CartItem): boolean => {
    if (!item.variant) return false;
    return item.variant.quantity >= item.quantity;
}
// ==================== ЭЛЕМЕНТЫ ЗАКАЗА ====================

export interface OrderItem {
    id: string;
    order_id: string;
    product_id: number;
    variant_id: number;
    product_name: string;
    product_image?: string;
    variant_name?: string;
    quantity: number;
    price_at_time: number;
    total: number;
    created_at: string;
    instance_id?: string;
    status?: string; // ✅ Добавляем статус
    commission_percent: number,
    product?: Product;
    variant?: ProductVariant;
}

// ==================== ЗАКАЗЫ КЛИЕНТОВ ====================
export interface Order {
    id: string;
    order_number: string;
    customer_id: string;
    total_amount: number;
    status?: string;
    delivery_address: string;
    customer_phone: string;
    customer_name: string;
    payment_method: 'cash' | 'card' | 'online';
    delivery_method: 'courier' | 'pickup' | 'post';
    delivery_price: number;
    items_total: number;
    notes?: string;
    created_at: string;
    updated_at: string;
    items?: OrderItem[];
}


// src/types/reviews.ts
export interface ProductReview {
    id: string;
    product_id: number;
    variant_id: number | null;
    customer_id: string;
    rating: 1 | 2 | 3 | 4 | 5;
    title: string | null;
    comment: string;
    advantages: string | null;
    disadvantages: string | null;
    images: string[];
    is_approved: boolean;
    is_verified_purchase: boolean;
    helpful_count: number;
    created_at: string;
    updated_at: string;
    customer?: {
        first_name: string;
        last_name: string;
    };
}

export interface VariantRating {
    variant_id: number;
    average_rating: number;
    total_reviews: number;
    rating_1_count: number;
    rating_2_count: number;
    rating_3_count: number;
    rating_4_count: number;
    rating_5_count: number;
}

export interface ProductRating {
    product_id: number;
    average_rating: number;
    total_reviews: number;
    rating_1_count: number;
    rating_2_count: number;
    rating_3_count: number;
    rating_4_count: number;
    rating_5_count: number;
}

export interface CreateReviewData {
    product_id: number;
    variant_id?: number;
    rating: number;
    title?: string;
    comment: string;
    advantages?: string;
    disadvantages?: string;
    images?: string[];
}



// ==================== ИЗБРАННОЕ ====================
// src/types/index.ts - добавьте в конец файла

// Избранное
export interface WishlistItem {
    id: string;
    customer_id: string;
    variant_id: number;
    created_at: string;
    variant?: ProductVariant;
    product?: Product;
}

export interface WishlistWithDetails extends WishlistItem {
    variant: ProductVariant & {
        product: Product;
    };
}

// ==================== ВСПОМОГАТЕЛЬНЫЕ ТИПЫ ====================
export interface AuthResponse {
    user: Customer;
    session: {
        token: string;
        expires_at: string;
    };
}

export interface LoginCredentials {
    phone: string;
    password: string;
}

export interface RegisterCredentials {
    phone: string;
    password: string;
    first_name?: string;
    last_name?: string;
    country_code?: string;
}

export interface UpdateProfileData {
    first_name?: string;
    last_name?: string;
    phone_number?: string;
}

// ==================== УТИЛИТЫ ====================
export const getProductMainImage = (product: Product): string => {
    if (product.images && product.images.length > 0) {
        return product.images[0];
    }
    return 'https://via.placeholder.com/300';
};

export const getProductPrice = (product: Product): number => {
    if (product.variants && product.variants.length > 0) {
        return Math.min(...product.variants.map(v => v.price));
    }
    return 0;
};

export const getProductStock = (product: Product): number => {
    if (product.variants && product.variants.length > 0) {
        return product.variants.reduce((sum, v) => sum + v.quantity, 0);
    }
    return product.stock_quantity;
};

export const getVariantDisplayName = (variant: ProductVariant): string => {
    const parts = [];
    if (variant.color) parts.push(variant.color);
    if (variant.size_or_volume) parts.push(variant.size_or_volume);
    if (variant.material) parts.push(variant.material);
    return parts.join(', ') || 'Стандарт';
};

// ==================== ТИПЫ ДЛЯ КОМПОНЕНТОВ ====================
export interface CartItemWithDetails extends CartItem {
    product: Product;
    variant: ProductVariant;
}

export interface OrderWithItems extends Order {
    items: OrderItem[];
}